import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useFormContext, useFormState } from 'react-hook-form'
import { BreadcrumbView } from '../../../../../../context/breadcrumb.context'
import { IconEdit } from '../../../../../../shared/icon/icon'
import { useApiFormProvider, useApiFormProviderDiscover, useApiFormProviderIcon } from '../../../../../../api-new/state/auth'
import { FormNew } from '../../../../../../shared/form-new/form'
import { WorkspaceSection } from '../../../../../../shared/sso-workspace/workspace-shell'
import { LeaveGuard } from '../../../../../../shared/sso-workspace/leave-guard'
import '../../../../../../shared/sso-workspace/sso-workspace.scss'
import { SectionIdentity } from './section-identity'
import { SectionCredentials } from './section-credentials'
import { SectionSignout } from './section-signout'
import { SectionHelp } from './section-help'
import { invalidFieldsMessage } from './fields'
import { ErrorReference } from '../../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'

// The old add page's defaults, plus the recommended sign-out choice.
// A new connection starts INACTIVE (audit B3): it has no domain yet, and
// nothing should be switched on before staff have bound one and checked it.
// It is OpenID Connect, the only protocol a connection has (fields.js
// DRIVER_OPTIONS), so the driver is sent, never chosen.
const DEFAULTS = { driver: 'oidc', connection: 'own_registration', clientAuthMethod: 'client_secret', active: false, domains: [], testAllowlist: [], endIdpSessionOnSignout: false, iconSource: 'auto', iconSVGCustom: '' }

// Only what can exist before the row does. Domains, organizations and claim
// mappings need a saved connection, so they come after creation.
const STEPS = [
  { id: 'idp', label: 'Identity provider', fields: ['providerName', 'displayName', 'issure', 'clientID'] },
  { id: 'creds', label: 'Credentials', fields: ['expiresAt'] },
  { id: 'signout', label: 'Sign-out', fields: [] },
  { id: 'help', label: 'Contacts & help', fields: [] }
]

// A half-filled wizard is unsaved work, like an edit. Once the connection
// exists the guard steps aside so the redirect to it isn't blocked.
const DirtyGuard = ({ created }) => {
  const { isDirty } = useFormState()
  return <LeaveGuard when={isDirty && !created} />
}

const Steps = ({ onLookup, onLoadIcons, message, failure, onMessage }) => {
  const { trigger, getFieldState } = useFormContext()
  const { isSubmitting } = useFormState()
  const [step, setStep] = useState(0)
  const [discovery, setDiscovery] = useState(null)
  const [filled, setFilled] = useState({ keys: [], pulse: 0 })
  const current = STEPS[step]

  const go = index => { onMessage(null); setStep(index) }

  // Validates only this step's fields. A field the step doesn't render (the
  // expiry for a shared app or a certificate) is not mounted, so it passes.
  const next = async () => {
    if (await trigger(current.fields)) return go(step + 1)
    const errors = {}
    current.fields.forEach(name => { const { error } = getFieldState(name); if (error) errors[name] = error })
    onMessage(invalidFieldsMessage(errors))
  }

  // Enter in a single-line field would submit the whole form, creating the
  // connection from a half-filled wizard. Before the last step it continues.
  const handleKeyDown = e => {
    if (e.key !== 'Enter' || step === STEPS.length - 1 || e.target.tagName !== 'INPUT') return
    e.preventDefault()
    next()
  }

  return (
    <div className="sw-layout">
      <nav className="sw-nav" aria-label="steps">
        {STEPS.map((s, i) =>
          <a key={s.id} href={`#${s.id}`} className={i === step ? 'active' : ''} aria-current={i === step ? 'step' : undefined}
            onClick={e => { e.preventDefault(); if (i < step) go(i) }}>{s.label}</a>)}
        <p className="small sw-hint mt-2">Domains, organizations and claim mappings are set up once the connection exists.</p>
      </nav>
      <main onKeyDown={handleKeyDown}>
        <p className="small sw-hint mb-2">Step {step + 1} of {STEPS.length}</p>
        {current.id === 'idp' &&
          <WorkspaceSection id="idp" title="Identity provider" lede="Who people sign in with. Look up the issuer to check it and fill in what it publishes." mode="staff">
            <SectionIdentity mode="staff" onLookup={onLookup} onLoadIcons={onLoadIcons} usedClaims={[]} onDiscovered={setDiscovery} onFilled={setFilled} showOwner
              noRoute="A new connection has no domain yet, so nobody is sent to it until you bind one after it's created." />
          </WorkspaceSection>}
        {current.id === 'creds' &&
          <WorkspaceSection id="creds" title="Credentials" lede="How EducationXR proves who it is to the identity provider." mode="staff">
            <SectionCredentials discovery={discovery} />
          </WorkspaceSection>}
        {current.id === 'signout' &&
          <WorkspaceSection id="signout" title="Sign-out" lede="What happens when someone signs out of EducationXR." mode="staff">
            <SectionSignout fromIssuer={filled.keys.includes('signoutURL')} pulseKey={filled.pulse} issuerEndpoint={(discovery && discovery.endSessionEndpoint) || ''} />
          </WorkspaceSection>}
        {current.id === 'help' &&
          <WorkspaceSection id="help" title="Contacts & help" lede="Who we warn about expiring credentials, and what people see if they aren't set up yet." mode="staff">
            <SectionHelp />
          </WorkspaceSection>}
        {message && <div role="alert" className="alert alert-danger py-2 small">{message}<ErrorReference error={failure} /></div>}
        <div className="d-flex gap-2 justify-content-end">
          {step > 0 && <button type="button" className="btn sw-btn" disabled={isSubmitting} onClick={() => go(step - 1)}>Back</button>}
          {step < STEPS.length - 1
            ? <button key="continue" type="button" className="btn sw-btn-primary" onClick={next}>Continue</button>
            : <button key="create" type="submit" className="btn sw-btn-primary" disabled={isSubmitting}>Create connection</button>}
        </div>
      </main>
    </div>
  )
}

// Guided mode (spec 2026-09-24): a new connection walks the workspace's
// sections as steps. Create connection posts, then opens the workspace.
export const ProviderWorkspaceAdd = () => {
  const history = useHistory()
  const apiFormProvider = useApiFormProvider()
  const discover = useApiFormProviderDiscover()
  const iconApi = useApiFormProviderIcon()
  const [message, failure, setMessage] = useErrorLine()
  const [createdID, setCreatedID] = useState(null)

  const handleLookup = issuer => discover.get(encodeURIComponent(issuer))
  const handleLoadIcons = issuer => iconApi.get(issuer ? encodeURIComponent(issuer) : undefined)

  const handleSubmit = async data => {
    setMessage(null)
    let created
    try {
      created = await apiFormProvider.post(data)
    } catch (exp) {
      setMessage((exp && exp.data && exp.data.message) || 'Could not create the connection.', exp)
      return
    }
    setCreatedID(created.id)
  }

  // Navigates after the guard (a child, so its effect runs first) has
  // released its block.
  useEffect(() => { if (createdID) history.push(`/admin/sso/connections/${createdID}`) }, [createdID]) // eslint-disable-line react-hooks/exhaustive-deps

  // Only the last step's fields are mounted when it submits, and none has a
  // rule today. This keeps a create blocked by one from being silent.
  const handleInvalid = errors => setMessage(invalidFieldsMessage(errors))

  return (
    <BreadcrumbView Icon={IconEdit} text="New connection">
      <div className="sw-workspace sw-pane p-3">
        <header className="mb-3">
          <h1 className="sw-title">New connection</h1>
          <div className="sw-sub">It starts inactive. Once it's created, bind its domain and link its organization, then switch it on.</div>
        </header>
        <FormNew state={DEFAULTS} enabled onSubmit={handleSubmit} onInvalid={handleInvalid}>
          <DirtyGuard created={!!createdID} />
          <Steps onLookup={handleLookup} onLoadIcons={handleLoadIcons} message={message} failure={failure}
            onMessage={setMessage} />
        </FormNew>
      </div>
    </BreadcrumbView>
  )
}
